import {AlertCircleIcon} from "lucide-react";
import {useEffect, useRef} from "react";
import {useForm, useWatch, type UseFormReturn} from "react-hook-form";
import {isAxiosError} from "axios";
import {toast} from "sonner";

import {Alert, AlertDescription} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import DatePicker from "@/components/ui/date-picker";
import {FakeProgress} from "@/components/ui/fake-progress";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import ImagePicker from "@/components/ui/image-picker";
import {Input} from "@/components/ui/input";
import {Item, ItemContent, ItemDescription, ItemTitle} from "@/components/ui/item";
import {Label} from "@/components/ui/label";
import {Sheet, SheetContent, SheetFooter} from "@/components/ui/sheet";
import {
  FORM_SHEET_FOOTER_CLASS,
  FORM_SHEET_ROOT_FORM_CLASS,
} from "@/src/modules/core/lib/form-sheet-layout";
import {Switch} from "@/components/ui/switch";
import {Textarea} from "@/components/ui/textarea";
import {TOAST_CONFIG} from "@/lib/constansts";
import {getDateTime, moment} from "@/src/modules/core/lib";
import {CoordinatesRequiredGate, Loader} from "@/src/modules/core/components";
import {useCoordinatesGate} from "@/src/modules/core/hooks/use-coordinates-gate";
import {useFormDraft, type ClearFormDraft} from "@/src/modules/core/hooks/use-form-draft";
import {useRestoreFormDraft} from "@/src/modules/core/hooks/use-restore-form-draft";
import {createFormInvalidHandler} from "@/src/modules/core/lib/glitchtip/product-telemetry";
import {resolveGpsForSubmit} from "@/src/modules/core/lib/resolve-gps-coordinates-string";
import {useStoredMe, useStoredMeProperties} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {useCreateFiles} from "@/src/modules/files/hooks";
import useDarCreate from "@/src/modules/dars/hooks/use-dar-create";
import useDarUpdate from "@/src/modules/dars/hooks/use-dar-update";
import useDar from "@/src/modules/dars/hooks/use-dar";
import {
  deserializeDarFormDraftValues,
  getDarFormDraftKey,
  serializeDarFormDraftValues,
} from "@/src/modules/dars/lib/dar-form-draft";
import type {Property} from "@/src/modules/properties/types";

import {DarCodeCombobox} from "./code-combobox";
import {DarTypeCombobox} from "./type-combobox";
import {
  DAR_FORM_HELP_KEYS,
  DAR_HELP_TOURS,
} from "../lib/dar-help";
import type {DailyActivityReport} from "../types";
import {FieldHelpLabel} from "@/src/modules/help/components/field-help";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";
import {SheetHelpHeader} from "@/src/modules/help/components/sheet-help-header";
import {useCancelHelpTourOnSheetClose} from "@/src/modules/help/hooks/use-cancel-help-tour-on-sheet-close";

type DarFormValues = {
  dar_type: {daily_activity_report_type_id: number; type: string} | null;
  dar_code: {daily_activity_report_code_id: number; type: string} | null;
  details: string;
  dar_date: Date;
  dar_time: string;
  send_notification: boolean;
  include_gps_coordinates: boolean;
  files: unknown[];
  property: Property | null;
};

type DarSheetProps = {
  isVisible: boolean;
  darId?: number | null;
  dar?: DailyActivityReport | null;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
  onClose: () => void;
};

type DarFormProps = {
  isVisible: boolean;
  isEditing: boolean;
  dar?: DailyActivityReport | null;
  darId?: number | null;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
  onSubmit: (data: DarFormValues) => Promise<void>;
  onInit: (helpers: {form: UseFormReturn<DarFormValues>; clearDraft: ClearFormDraft}) => void;
};

function darFormHasPersistableContent(values: Record<string, unknown>) {
  const details = typeof values.details === "string" ? values.details.trim() : "";
  const files = Array.isArray(values.files) ? values.files : [];
  return (
    details.length > 0 ||
    files.length > 0 ||
    values.dar_type != null ||
    values.dar_code != null ||
    values.include_gps_coordinates === true ||
    values.send_notification === true
  );
}

function getDefaultValues({
  dar,
  property,
}: {
  dar?: DailyActivityReport | null;
  property: Property | null;
}): DarFormValues {
  const typeId = dar?.type.id ?? dar?.type.daily_activity_report_type_id;
  const codeId = dar?.code.id ?? dar?.code.daily_activity_report_code_id;
  return {
    dar_type:
      dar && typeId != null
        ? {daily_activity_report_type_id: typeId, type: dar.type.name}
        : null,
    dar_code:
      dar && codeId != null
        ? {daily_activity_report_code_id: codeId, type: dar.code.name}
        : null,
    details: dar?.details ?? "",
    dar_date: dar?.dar_date ? moment(dar.dar_date).toDate() : moment().startOf("day").toDate(),
    dar_time: dar?.dar_date ? moment(dar.dar_date).format("HH:mm") : moment().format("HH:mm"),
    send_notification: Boolean(dar?.send_notification ?? 0),
    include_gps_coordinates: Boolean(dar?.include_gps_coordinates ?? 0),
    files: dar?.files ?? [],
    property,
  };
}

const startOfYear = moment().startOf("year").toDate();

export function DarSheet({
  isVisible,
  darId,
  dar: _dar,
  draftRestoreValues,
  onDraftRestored,
  onClose,
}: DarSheetProps) {
  const formRef = useRef<UseFormReturn<DarFormValues> | null>(null);
  const draftHelpersRef = useRef<{clearDraft: ClearFormDraft} | null>(null);

  const {isPending, dar} = useDar(darId);

  const {mutateAsync: mutateCreateFiles} = useCreateFiles();
  const {mutateAsync: mutateInsertDar} = useDarCreate();

  const {mutateAsync: mutateUpdateDar} = useDarUpdate(darId);

  useCancelHelpTourOnSheetClose(isVisible);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    draftHelpersRef.current?.clearDraft();
    onClose();
  };

  const onSubmit = async (data: DarFormValues) => {
    try {
      if (data.files.length > 5) {
        toast.error("You can only upload up to 5 images", TOAST_CONFIG);
        return;
      }

      let files = data.files.filter((i) => !(i instanceof File));
      const toProcess = data.files.filter((i): i is File => i instanceof File);

      if (toProcess.length && navigator.onLine) {
        const newFiles = await mutateCreateFiles(toProcess);
        files = [...files, ...newFiles];
      } else if (!navigator.onLine) {
        files = data.files;
      }

      const gps = await resolveGpsForSubmit(
        {
          propertyCoordinates: data.property?.gps_coordinates ?? null,
        },
        {
          captureContext: {
            feature: "dars",
            operation: darId ? "dar_update" : "dar_create",
          },
        },
      );
      if (!gps) {
        return;
      }

      if (!data.dar_type || !data.dar_code || !data.property) {
        toast.error("Please complete all required fields", TOAST_CONFIG);
        return;
      }

      const darData = {
        daily_activity_report_type_id: data.dar_type.daily_activity_report_type_id,
        daily_activity_report_code_id: data.dar_code.daily_activity_report_code_id,
        dar_date: getDateTime(data.dar_date, data.dar_time).utc().format(),
        automatic_dar: 0,
        details: data.details,
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
        include_gps_coordinates: Number(data.include_gps_coordinates),
        send_notification: Number(data.send_notification),
        property_id: data.property.id,
        files,
      };

      if (!navigator.onLine) {
        toast.error(
          darId
            ? "You cannot update a DAR without an internet connection."
            : "You need an internet connection to create a DAR.",
          TOAST_CONFIG,
        );
        return;
      }

      if (!darId) {
        await mutateInsertDar(darData);
      } else {
        await mutateUpdateDar({
          ...darData,
          daily_activity_report_id: darId,
        });
      }

      draftHelpersRef.current?.clearDraft({submitted: true});
      onClose();
    } catch (error: unknown) {
      const msg =
        (isAxiosError(error) &&
          (error.response?.data as {message?: string} | undefined)?.message) ||
        "An error occurred while submitting the DAR";
      toast.error(msg, TOAST_CONFIG);
    }
  };

  const handleInit = (helpers: {
    form: UseFormReturn<DarFormValues>;
    clearDraft: ClearFormDraft;
  }) => {
    formRef.current = helpers.form;
    draftHelpersRef.current = helpers;
  };

  return (
    <Sheet
      open={isVisible}
      askConfirmation={() => {
        if (!formRef.current) return false;
        return Object.keys(formRef.current.formState.dirtyFields).length > 0;
      }}
      onOpenChange={handleOpenChange}
    >
      <SheetContent className="flex min-h-0 flex-col gap-0 overflow-hidden p-0">
        <SheetHelpHeader
          title={darId ? "Update DAR" : "New DAR"}
          createTourId={DAR_HELP_TOURS.formCreate}
          showCreateTour={!darId}
        />
        {isPending ? (
          <Loader className="min-h-48 flex-1" />
        ) : (
          <DarForm
            isVisible={isVisible}
            isEditing={!!darId}
            dar={dar ?? _dar}
            darId={darId}
            draftRestoreValues={draftRestoreValues}
            onDraftRestored={onDraftRestored}
            onSubmit={onSubmit}
            onInit={handleInit}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function DarForm({
  isVisible,
  isEditing,
  dar,
  darId,
  draftRestoreValues,
  onDraftRestored,
  onSubmit,
  onInit,
}: DarFormProps) {
  const me = useStoredMe();
  const userProperty = useStoredUserProperty();
  const userProperties = useStoredMeProperties();
  const minDateRef = useRef(me?.isAdmin || me?.isSupervisor ? startOfYear : new Date());
  const isEditDateEnabled = Boolean(me?.isAdmin || me?.isSupervisor);

  const properties = userProperties.filter((p) => p.is_hidden === 0);
  const hasPropertySelector = properties.length > 1;

  const form = useForm<DarFormValues>({
    defaultValues: getDefaultValues({
      dar,
      property: userProperty,
    }),
  });
  const {
    handleSubmit,
    control,
    formState: {errors, isSubmitting},
    watch,
    setValue,
    getValues,
    reset,
  } = form;

  const files = useWatch({control, name: "files", defaultValue: []});

  const draftKey = getDarFormDraftKey(isEditing, darId, userProperty?.id);

  const {snapshotDraft, clearDraft} = useFormDraft({
    draftKey,
    moduleLabel: "DAR",
    route: "/dars",
    sheetId: "dar",
    entityId: darId ?? undefined,
    contextPropertyId: userProperty?.id,
    enabled: true,
    getValues,
    serializeValues: serializeDarFormDraftValues,
    shouldPersist: () => darFormHasPersistableContent(getValues()),
    watch,
    abandonTelemetry: {
      feature: "dars",
      operation: isEditing ? "dar_update" : "dar_create",
    },
  });

  useRestoreFormDraft({
    draftRestoreValues,
    onRestore: () => {
      reset({
        ...getDefaultValues({dar, property: userProperty}),
        ...deserializeDarFormDraftValues(draftRestoreValues!),
      });
      onDraftRestored?.();
    },
  });

  const handleUploadFiles = (newFiles: unknown[]) => {
    setValue("files", newFiles, {shouldDirty: true});
    snapshotDraft();
  };

  const removeFiles = (index: number) => {
    setValue(
      "files",
      getValues("files").filter((_: unknown, i: number) => i !== index),
      {shouldDirty: true},
    );
    snapshotDraft();
  };

  useEffect(() => {
    onInit?.({form, clearDraft});
  }, [form, clearDraft, onInit]);

  const {submitBlockedByLocation} = useCoordinatesGate(true, {active: isVisible});

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(
          onSubmit,
          createFormInvalidHandler({
            feature: "dars",
            operation: isEditing ? "dar_update" : "dar_create",
            sheetId: "dar",
          }),
        )}
        className={FORM_SHEET_ROOT_FORM_CLASS}
      >
        <CoordinatesRequiredGate />
        <SheetFormScrollBody className="flex flex-col gap-4 px-4 pt-2 pb-4">
        <fieldset
          disabled={submitBlockedByLocation}
          className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0"
        >
        {hasPropertySelector ? (
          <FormField
            control={form.control}
            name="property"
            rules={{required: "Property is required"}}
            render={({field, formState: {errors}}) => (
              <FormItem>
                <FieldHelpLabel helpKey={DAR_FORM_HELP_KEYS.property} showIcon={false}>
                  <FormLabel>Property</FormLabel>
                </FieldHelpLabel>
                <FormControl>
                  <ComboboxGeneric
                    items={properties}
                    {...field}
                    placeholder="Select property"
                    preserveCase
                    getItemKey={(item) => item.id}
                    getItemLabel={(item) => item.name}
                    isItemEqual={(item, selected) => item.id === selected?.id}
                    renderItem={(option) => (
                      <Item size="sm" className="p-0">
                        <ItemContent>
                          <ItemTitle className="font-normal">{option.name}</ItemTitle>
                          <ItemDescription className="text-xs">
                            {option.street} {option.number}, {option.city}, {option.state}{" "}
                            {option.zipcode}
                          </ItemDescription>
                        </ItemContent>
                      </Item>
                    )}
                  />
                </FormControl>
                {errors.property && <FormError error={errors.property.message} />}
              </FormItem>
            )}
          />
        ) : null}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name="dar_type"
            rules={{required: "Type is required"}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={DAR_FORM_HELP_KEYS.type} showIcon={false}>
                  <Label htmlFor="dar_type">Type</Label>
                </FieldHelpLabel>
                <DarTypeCombobox {...field} />
                {errors.dar_type && <FormError error={errors.dar_type.message} />}
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="dar_code"
            rules={{required: "Code is required"}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={DAR_FORM_HELP_KEYS.code} showIcon={false}>
                  <Label htmlFor="dar_code">Code</Label>
                </FieldHelpLabel>
                <DarCodeCombobox {...field} />
                {errors.dar_code && <FormError error={errors.dar_code.message} />}
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name="dar_date"
            rules={{required: "Date is required"}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={DAR_FORM_HELP_KEYS.date} showIcon={false}>
                  <Label htmlFor="dar_date">Date</Label>
                </FieldHelpLabel>
                <DatePicker
                  id="dar_date"
                  pickerDisabled={!isEditDateEnabled}
                  disabled={{before: minDateRef.current}}
                  selected={field.value}
                  onSelect={field.onChange}
                />
                {errors.dar_date && <FormError error={errors.dar_date.message} />}
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="dar_time"
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={DAR_FORM_HELP_KEYS.time} showIcon={false}>
                  <Label htmlFor="dar_time">Time</Label>
                </FieldHelpLabel>
                <Input
                  type="time"
                  id="dar_time"
                  disabled={!isEditDateEnabled}
                  {...field}
                />
              </FormItem>
            )}
          />
        </div>

        <div>
          <Alert variant="info" className="mb-2 text-xs">
            <AlertCircleIcon className="size-4" />
            <AlertDescription>
              Remember to explain WHO, WHAT, WHEN, WHERE, WHY, HOW and CONCLUSION
            </AlertDescription>
          </Alert>
          <FormField
            control={control}
            name="details"
            rules={{required: "Details is required"}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={DAR_FORM_HELP_KEYS.details} showIcon={false}>
                  <Label htmlFor="details">Details</Label>
                </FieldHelpLabel>
                <Textarea {...field} className="resize-none" rows={5} maxLength={500} />
                <p className="ml-auto text-sm text-muted-foreground">{field.value.length} / 500</p>
                {errors.details && <FormError error={errors.details.message} />}
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <FormItem>
            <FieldHelpLabel helpKey={DAR_FORM_HELP_KEYS.images} showIcon={false}>
              <Label htmlFor="files">Images</Label>
            </FieldHelpLabel>
            <ImagePicker
              images={files}
              errors={errors}
              immediateUpload
              captureFeature="dars"
              captureOperation={isEditing ? "dar_update" : "dar_create"}
              onBeforePick={snapshotDraft}
              onUpload={handleUploadFiles}
              onDelete={removeFiles}
            />
            {files.length > 0 && isSubmitting ? <FakeProgress /> : null}
          </FormItem>
        </div>
        <div className="flex flex-col gap-2">
          <FormField
            control={control}
            name="send_notification"
            render={({field}) => (
              <FormItem className="flex flex-row items-center justify-between">
                <FieldHelpLabel helpKey={DAR_FORM_HELP_KEYS.sendNotification} watchValue={field.value} showIcon={false}>
                  <Label htmlFor="send_notification">Send notification to property manager</Label>
                </FieldHelpLabel>
                <FormControl>
                  <Switch
                    id="send_notification"
                    name="send_notification"
                    disabled={isEditing}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormField
            control={control}
            name="include_gps_coordinates"
            render={({field}) => (
              <FormItem className="flex flex-row items-center justify-between">
                <FieldHelpLabel
                  helpKey={DAR_FORM_HELP_KEYS.includeGpsCoordinates}
                  watchValue={field.value}
                  showIcon={false}
                >
                  <Label htmlFor="include_gps_coordinates">Include GPS coordinates</Label>
                </FieldHelpLabel>
                <FormControl>
                  <Switch
                    id="include_gps_coordinates"
                    name="include_gps_coordinates"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        </fieldset>
        </SheetFormScrollBody>
        <SheetFooter className={FORM_SHEET_FOOTER_CLASS}>
          <Button
            type="submit"
            className="w-full"
            data-help-target={DAR_FORM_HELP_KEYS.submit}
            disabled={isSubmitting || submitBlockedByLocation}
          >
            {isEditing ? "Update DAR" : "Submit DAR"}
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
